import {
  MiniKit,
  type PayCommandInput,
  Tokens,
  tokenToDecimals,
} from '@worldcoin/minikit-js';
import i18next, { t } from 'i18next';
import { useCallback, useState } from 'react';
import { mutate } from 'swr';
import { toaster } from '@/components/toaster';
import { CONSTANTS } from '@/constants';
import { publicClient } from '@/utils/client';
import { mergeAPIError } from '@/utils/errors';
import { fetchStatus } from '@/utils/wait-transaction';
import { transferWLD } from './transferToken';
import { request } from '@/utils/request';
import { useOpenChestStore } from '@/stores/use-open-chest';
import { useShallow } from 'zustand/shallow';

export interface ReferenceResponse {
  data: {
    referenceId: string;
    amount: number;
  };
}

async function sendPayment(
  address: string,
  quantity: number,
  isSend?: boolean,
) {
  try {
    if (!MiniKit.isInstalled() || !CONSTANTS.RECEIVER) {
      return { success: false, error: 'Minikit not install' };
    }

    const body = {
      sender: address,
      token: CONSTANTS.TOKEN_ADDRESS,
      amount: CONSTANTS.AMOUNT,
      appId: CONSTANTS.APP_ID,
      description: '',
      quantity,
    };

    const res = await request(
      `${CONSTANTS.BASE_URL}/api/world-coin-chest-smasher/extra-eggs/payment`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );

    if (!res.ok) {
      const msg = await res
        .json()
        .then((e) => mergeAPIError(e))
        .catch((e) => e.message ?? res.statusText);
      throw new Error(`Failed with status ${res.status} :${msg}`);
    }

    const data: ReferenceResponse = await res.json();
    let finalPayloadResponse = null;
    if (i18next.language === 'id' || isSend) {
      const { finalPayload } = await transferWLD(data, CONSTANTS.RECEIVER);
      finalPayloadResponse = finalPayload;
    } else {
      const payload: PayCommandInput = {
        reference: data.data.referenceId,
        to: CONSTANTS.RECEIVER,
        tokens: [
          {
            symbol: Tokens.WLD,
            token_amount: tokenToDecimals(
              data.data.amount,
              Tokens.WLD,
            ).toString(),
          },
        ],
        description: t('purchase').replace('%d', `${quantity}`),
      };

      const { finalPayload } = await MiniKit.commandsAsync.pay(payload);

      finalPayloadResponse = finalPayload;
    }
    if (
      finalPayloadResponse.status === 'error' &&
      finalPayloadResponse.error_code === 'user_blocked' &&
      !isSend
    ) {
      // Pay error => loop call pay
      return await sendPayment(address, quantity, true);
    }

    if (finalPayloadResponse.status === 'success') {
      subcribe(address);

      await waitBuy(finalPayloadResponse.transaction_id, address);

      toaster.success(t('additional'));
    }
    return { success: true, data: finalPayloadResponse };
  } catch (error) {
    console.error(error);
    const message = mergeAPIError(error);
    toaster.error(message);
    return { success: false, error: error };
  }
}

const pollingInterval = 4e3;

function subcribe(address: string) {
  const eventSource = new EventSource(`${CONSTANTS.SSE_URL}=${address}`);
  console.log('Call Subcribe SSE Buy Chest');
  eventSource.onopen = () => {
    console.log('Start SSE Buy Chest');
  };

  eventSource.addEventListener('extraEggPaymentEvent', (event) => {
    console.log(`Buy Chest Event is ${JSON.stringify(event)}`);
    eventSource.close();
    mutate({ key: 'extra-eggs', address: address });
  });

  eventSource.onerror = () => {
    console.log('Close SSE Buy Chest, Error!');
    eventSource.close();
  };
}

function waitBuy(transaction_id: string, address: string) {
  return new Promise((resolve, reject) => {
    async function pollHash() {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      try {
        const status = await fetchStatus(transaction_id);

        if (!status.transactionHash) {
          timeoutId = setTimeout(pollHash, pollingInterval);
        } else if (status.transactionHash) {
          const transaction = await publicClient.waitForTransactionReceipt({
            hash: status.transactionHash as `0x${string}`,
          });
          console.log('Transaction receipt:', transaction);

          const params = new URLSearchParams();
          params.set('wallet', address);
          params.set('appId', CONSTANTS.APP_ID || '');
          params.set('txhash', transaction.transactionHash);

          const resp = await request(
            `${CONSTANTS.BASE_URL}/api/world-coin-chest-smasher/extra-eggs/payment/crawl?${params.toString()}`,
          );
          if (!resp.ok) {
            throw new Error(
              `Faild to submit crawl to server with status ${resp.status}`,
            );
          }

          if (resp.ok) {
            clearTimeout(timeoutId);

            resolve('Successful');
          }
        } else {
          timeoutId = setTimeout(pollHash, pollingInterval);
        }
      } catch (error) {
        console.error('Error during polling:', error);
        reject('Failed to pay');
      }
    }

    pollHash();
  });
}

export function useBuyChest() {
  const [isLoading, setIsLoading] = useOpenChestStore(useShallow(state => [state.isLoading, state.setLoading]))


  const buy = useCallback(async (address: string, quantity: number) => {
    setIsLoading(true);

    const res = await sendPayment(address, quantity);

    setIsLoading(false);
    return res;
  }, []);

  return { buy, isLoading };
}
